// Extract playable MediaItems from a Bluesky AppView post embed. Handles
// image and video embeds (and the recordWithMedia wrapper); ignores external
// links and bare quotes — Game/Tiled/Gallery only show real media.
import type { ContentKind, MediaItem } from '../types'

/** Per-item "worth" used to hit the preload target (~10 min). */
export const IMAGE_WORTH_MS = 4_000
export const VIDEO_WORTH_MS = 8_000

interface BskyImage {
  thumb?: string
  fullsize?: string
  alt?: string
  aspectRatio?: { width: number; height: number }
}
interface BskyEmbed {
  $type?: string
  images?: BskyImage[]
  playlist?: string
  thumbnail?: string
  alt?: string
  aspectRatio?: { width: number; height: number }
  media?: BskyEmbed
}
export interface BskyPost {
  uri: string
  cid?: string
  author?: { handle?: string; displayName?: string }
  record?: { text?: string; createdAt?: string }
  embed?: BskyEmbed
  indexedAt?: string
  labels?: { val: string }[]
}

function imagesOf(embed: BskyEmbed | undefined): BskyImage[] {
  if (!embed) return []
  if (embed.images?.length) return embed.images
  if (embed.media) return imagesOf(embed.media)
  return []
}
function videoOf(embed: BskyEmbed | undefined): BskyEmbed | null {
  if (!embed) return null
  if (embed.playlist) return embed
  if (embed.media?.playlist) return embed.media
  return null
}

export function mediaItemsFromPost(post: BskyPost, tagLabel: string): MediaItem[] {
  const out: MediaItem[] = []
  const handle = post.author?.handle
  const imgs = imagesOf(post.embed)
  imgs.forEach((im, i) => {
    const src = im.thumb ?? im.fullsize
    if (!src) return
    out.push({
      id: `${post.uri}#i${i}`,
      postUri: post.uri,
      kind: 'image',
      src,
      fullsize: im.fullsize ?? im.thumb,
      alt: im.alt,
      width: im.aspectRatio?.width,
      height: im.aspectRatio?.height,
      authorHandle: handle,
      tag: tagLabel,
      worthMs: IMAGE_WORTH_MS,
    })
  })
  const vid = videoOf(post.embed)
  if (vid?.playlist) {
    out.push({
      id: `${post.uri}#v`,
      postUri: post.uri,
      kind: 'video',
      src: vid.thumbnail ?? '',
      playlistUrl: vid.playlist,
      alt: vid.alt,
      width: vid.aspectRatio?.width,
      height: vid.aspectRatio?.height,
      authorHandle: handle,
      tag: tagLabel,
      worthMs: VIDEO_WORTH_MS,
    })
  }
  return out
}

/** Keep only items matching the chosen content kind. */
export function filterKind(items: MediaItem[], kind: ContentKind): MediaItem[] {
  if (kind === 'both') return items
  return items.filter((m) => m.kind === kind)
}
